// Generic API handler for wizard steps
// Uses step configuration to make API calls
import {
  couponApiHeaders,
  isCouponTokenForbidden,
  COUPON_TOKEN_INVALID_MESSAGE,
  popinApiHeaders,
  isPopinTokenForbidden,
  POPIN_TOKEN_INVALID_MESSAGE,
  withPopinManageToken
} from './couponApiUtils';

/**
 * Save step data to API using step configuration
 * @param {Object} stepConfig - Step configuration from wizardSteps.js
 * @param {Object} wizardData - Current wizard data
 * @param {string|number} couponId - Coupon ID for API endpoint
 * @param {string|null} [creatorToken] - Creator token for coupon access (required after step/init)
 * @returns {Promise<{ success: boolean; tokenInvalid?: boolean }>} - Success status and optional tokenInvalid flag
 */
export const saveStepData = async (stepConfig, wizardData, couponId, creatorToken) => {
  // If no API config, step doesn't need API call
  if (!stepConfig.apiConfig) {
    return { success: true };
  }

  if (!couponId) {
    console.error('No couponId provided for API call');
    return { success: false };
  }

  try {
    const endpoint = stepConfig.apiConfig.endpoint.replace('{couponId}', couponId);
    const payload = stepConfig.apiConfig.payload(wizardData);
    const method = stepConfig.apiConfig.method || 'PATCH';

    // Normal coupon edit flow uses X-Coupon-Token (creator token).
    // Renew-from-email flow edits the bundled coupon via X-Popin-Manage-Token when no creator token is available.
    const popinManageToken = wizardData?.meta?.popinManageToken;
    const headers = withPopinManageToken(
      couponApiHeaders(creatorToken, { 'Content-Type': 'application/json' }),
      creatorToken ? null : popinManageToken
    );

    const res = await fetch(`${process.env.REACT_APP_API_URI}${endpoint}`, {
      method: method,
      headers,
      body: JSON.stringify(payload)
    });

    const response = await res.json();

    if (isCouponTokenForbidden(res, response)) {
      return { success: false, tokenInvalid: true, message: COUPON_TOKEN_INVALID_MESSAGE };
    }

    return { success: response.status === 'success' || res.ok };
  } catch (error) {
    console.error(`Error saving step data for ${stepConfig.id}:`, error);
    return { success: false };
  }
};

/**
 * Renew-from-email helpers (manage token auth).
 * GET returns `data` containing pop-in fields + nested `locations`/`advertising`/`coupon`.
 */
export const fetchPopinById = async ({ popinId, manageToken }) => {
  try {
    const res = await fetch(`${process.env.REACT_APP_API_URI}/v3/advertising/popin/${popinId}`, {
      method: 'GET',
      headers: withPopinManageToken({}, manageToken)
    });
    const body = await res.json();

    if (isPopinTokenForbidden(res, body)) {
      return { success: false, tokenInvalid: true, message: POPIN_TOKEN_INVALID_MESSAGE };
    }

    if (body?.status === 'success' && body?.data) {
      return { success: true, data: body.data };
    }

    return { success: false, error: body?.error || body?.message || 'Failed to load pop-in' };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const renewPopinConfirm = async ({
  popinId,
  manageToken,
  additionalClaims,
  popinRateId,
  customerEmail,
  customerName,
  affiliateCode
}) => {
  try {
    const payload = {
      additionalClaims,
      customerEmail
    };
    if (popinRateId != null) payload.popinRateId = popinRateId;
    if (customerName) payload.customerName = customerName;
    if (affiliateCode) payload.affiliateCode = affiliateCode;

    const res = await fetch(
      `${process.env.REACT_APP_API_URI}/v3/advertising/popin/${popinId}/renew/confirm`,
      {
        method: 'POST',
        headers: withPopinManageToken({ 'Content-Type': 'application/json' }, manageToken),
        body: JSON.stringify(payload)
      }
    );
    const body = await res.json();

    if (isPopinTokenForbidden(res, body)) {
      return { success: false, tokenInvalid: true, message: POPIN_TOKEN_INVALID_MESSAGE };
    }

    if (body?.status === 'success') {
      return { success: true, data: body.data };
    }

    return { success: false, error: body?.error || body?.message || 'Failed to renew pop-in' };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Create initial coupon
 * @param {number|null} companyId - Company ID (optional, uses wizardData if not provided)
 * @param {Object} wizardData - Current wizard data
 * @param {string|null} [source] - Optional source; pass 'popin_deal' when creating the coupon for a Pop-In bundle
 * @returns {Promise<{success: boolean, couponId?: number, creatorToken?: string, error?: string}>}
 */
export const createInitialCoupon = async (companyId = null, wizardData, source = null) => {
  try {
    const payload = {
      advertisingId: companyId || wizardData.meta.companyId || wizardData.company.companyId
    };
    if (source) payload.source = source;

    const res = await fetch(`${process.env.REACT_APP_API_URI}/v3/advertising/step/init`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });

    const data = await res.json();
    const createdCouponId = data?.data?.couponId ?? data?.couponId;
    const creatorToken = data?.data?.creatorToken ?? data?.creatorToken ?? null;

    if ((data.status === 'success' || res.ok) && createdCouponId) {
      return { success: true, couponId: createdCouponId, creatorToken };
    } else {
      return { success: false, error: data.error || 'Failed to create coupon' };
    }
  } catch (error) {
    console.error('Error creating initial coupon:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Persist pop-in locations (create draft pop-in on first save, then replace locations).
 * @param {Object} wizardData
 * @param {{ companyId: number, couponId?: number|null, popinId?: number|null, popinCreatorToken?: string|null }} meta
 * @returns {Promise<{ success: boolean; popinId?: number; popinCreatorToken?: string; tokenInvalid?: boolean; message?: string; error?: string }>}
 */
export const savePopinLocationsFromWizard = async (wizardData, meta) => {
  const advertisingId = meta.companyId || wizardData.meta?.companyId;
  const popinRateId = wizardData.popinBudget?.popinRateId;
  const list = wizardData.locations?.list || [];
  const locations = list.map((loc) => ({
    title: loc.title,
    address: loc.address,
    latitude: loc.latitude,
    longitude: loc.longitude
  }));

  if (!advertisingId || !popinRateId) {
    return { success: false, error: 'Missing company or pop-in budget selection' };
  }
  if (!locations.length) {
    return { success: false, error: 'Add at least one location' };
  }

  const api = process.env.REACT_APP_API_URI;
  const includeCoupon = wizardData.dealSelection?.includeCoupon !== false;
  const couponIdForPopin =
    wizardData.dealSelection?.selectedDeal === 'popin' && includeCoupon
      ? meta.couponId || wizardData.meta?.couponId
      : null;

  try {
    if (meta.popinId && meta.popinCreatorToken) {
      const res = await fetch(`${api}/v3/advertising/popin/${meta.popinId}/locations`, {
        method: 'POST',
        headers: popinApiHeaders(meta.popinCreatorToken, { 'Content-Type': 'application/json' }),
        body: JSON.stringify({ locations })
      });
      const response = await res.json();
      if (isPopinTokenForbidden(res, response)) {
        return { success: false, tokenInvalid: true, message: POPIN_TOKEN_INVALID_MESSAGE };
      }
      return { success: response.status === 'success' || res.ok, popinId: meta.popinId, popinCreatorToken: meta.popinCreatorToken };
    }

    const res = await fetch(`${api}/v3/advertising/popin`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        advertising_id: advertisingId,
        coupon_id: couponIdForPopin || null,
        popin_rate_id: popinRateId,
        locations
      })
    });
    const response = await res.json();
    if (isPopinTokenForbidden(res, response)) {
      return { success: false, tokenInvalid: true, message: POPIN_TOKEN_INVALID_MESSAGE };
    }
    const ok = response.status === 'success' || res.ok;
    const popinId = response?.data?.id ?? response?.data?.popinId;
    const popinCreatorToken = response?.data?.creatorToken ?? null;
    if (!ok || !popinId || !popinCreatorToken) {
      return { success: false, error: response.error || response.message || 'Failed to save pop-in' };
    }
    return { success: true, popinId, popinCreatorToken };
  } catch (error) {
    console.error('savePopinLocationsFromWizard:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Update an existing pop-in's budget (popin_rate_id → price/visitors).
 * No-op (returns success) when the pop-in draft has not been created yet.
 * @param {{ popinId: number|null, popinCreatorToken: string|null, popinRateId: number|null }} params
 * @returns {Promise<{ success: boolean; tokenInvalid?: boolean; message?: string; error?: string }>}
 */
export const updatePopinBudget = async ({ popinId, popinCreatorToken, popinRateId }) => {
  if (!popinId || !popinCreatorToken) {
    // Pop-in row doesn't exist yet (first pass through the wizard); the rate will be
    // persisted when the pop-in is created on the locations step.
    return { success: true };
  }
  if (!popinRateId) {
    return { success: false, error: 'popin_rate_id is required' };
  }

  try {
    const res = await fetch(
      `${process.env.REACT_APP_API_URI}/v3/advertising/popin/${popinId}/budget`,
      {
        method: 'PATCH',
        headers: popinApiHeaders(popinCreatorToken, { 'Content-Type': 'application/json' }),
        body: JSON.stringify({ popin_rate_id: popinRateId })
      }
    );
    const response = await res.json();
    if (isPopinTokenForbidden(res, response)) {
      return { success: false, tokenInvalid: true, message: POPIN_TOKEN_INVALID_MESSAGE };
    }
    return {
      success: response.status === 'success' || res.ok,
      error: response?.error || response?.message
    };
  } catch (error) {
    console.error('updatePopinBudget:', error);
    return { success: false, error: error.message };
  }
};
